import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI
  FaTruckPickup, // For Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaCarSide, // For Tyre and Rim Protection
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const VolkswagenCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is zero depreciation useful for Volkswagen Taigun or Virtus?",
      answer:
        "Yes, especially during the first 3-5 years to avoid deduction on replaced parts.",
    },
    {
      question: "Does VW turbo engine impact insurance premium?",
      answer:
        "Yes, slightly. Turbocharged models generally attract higher premiums due to performance capabilities.",
    },
    {
      question: "Is engine protection necessary for VW cars?",
      answer:
        "Highly recommended for areas with heavy rainfall or waterlogging.",
    },
    {
      question: "Can I buy insurance online for my Volkswagen?",
      answer:
        "Yes, most insurers offer hassle-free digital purchase and renewal options.",
    },
    {
      question: "Is NCB transferable from my old Polo to a new Virtus?",
      answer: "Yes, as long as the new car is registered in your name.",
    },
    {
      question: "Does fuel type affect Volkswagen insurance?",
      answer:
        "Currently, all VW models are petrol-only, so there's little premium variation due to fuel.",
    },
    {
      question: "Can I cover VW infotainment systems and accessories?",
      answer:
        "Yes, if declared under insured accessories during policy purchase.",
    },
    {
      question: "How long does claim settlement take?",
      answer:
        "Usually within 7–14 working days after submitting all required documents and completing the survey.",
    },
    {
      question: "Are VW repairs expensive?",
      answer:
        "They can be higher than mass-market brands due to imported parts and DSG gearbox costs.",
    },
    {
      question: "Do insurance plans cover glass and windshield damage?",
      answer:
        "Yes, if caused by accidents. Some insurers offer specific windshield cover too.",
    },
    {
      question: "Is third-party insurance enough for my Volkswagen?",
      answer:
        "No, a comprehensive plan is advised due to the value and electronics in VW vehicles.",
    },
    {
      question: "Is Return to Invoice cover available for Tiguan or Virtus?",
      answer: "Yes, generally for cars less than 3 years old.",
    },
    {
      question: "Can I switch insurers at renewal?",
      answer: "Yes, and your NCB can be transferred to the new insurer.",
    },
    {
      question: "Is VW car insurance costlier than Maruti or Hyundai?",
      answer: "Slightly, due to higher IDV and premium components.",
    },
    {
      question: "What if I miss renewing on time?",
      answer:
        "You may lose NCB and require vehicle inspection for policy reinstatement.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description:
        "Eliminates depreciation deductions during claims—great for Virtus and Taigun owners.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection",
      description:
        "Important for turbo-petrol engines prone to damage in flood-prone regions.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description: "Covers full invoice value in case of total loss or theft.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description:
        "Offers help for breakdowns, fuel delivery, towing, or flat tyres.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Covers nuts, bolts, oils, and filters used during repairs.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Tyre and Rim Protection",
      description:
        "Useful for Tiguan or Virtus owners in areas with bad road conditions.",
      icon: <FaCarSide className="text-4xl text-indigo-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
const volkswageninsurance = [
    {
      name: 'Virtus',
      path: '/volkswagen-virtus-car-insurance',
      image: '/assets/volkswagenvirtus.jpeg',
    },
    {
      name: 'Taigun',
      path: '/volkswagen-taigun-car-insurance',
      image: '/assets/volkswagentaigun.jpeg',
    },
    {
      name: 'Golf GT',
      path: '/volkswagen-golfgt-car-insurance',
      image: '/assets/volkswagengolfgt.jpeg',
    },
      {
      name: 'Tiguan',
      path: '/volkswagen-tiguan-car-insurance',
      image: '/assets/volkswagentiguan.jpeg',
    },
     {
      name: 'Polo',
      path: '/volkswagen-polo-car-insurance',
      image: '/assets/volkswagenpolo.jpeg',
    },
     {
      name: 'ID4',
      path: '/volkswagen-id4-car-insurance',
      image: '/assets/volkswagenid4.jpeg',
    },   
  ];
  return (
    <>
      <Helmet>
        <title>
          Volkswagen Car Insurance in India – Compare Quotes & Buy Online
        </title>
        <meta
          name="description"
          content="Explore Volkswagen car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Volkswagen vehicle."
        />
        <meta
          name="keywords"
          content="volkswagen car insurance, auto insurance india, volkswagen virtus insurance, volkswagen taigun insurance, volkswagen tiguan insurance, car insurance quotes"
        />
         <link
          rel="canonical"
          href="https://thefinmaster.com/volkswagen-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
    
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Volkswagen Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Volkswagen Car Insurance 
                  </span>{" "}
              
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Volkswagen stands out for its strong build quality, refined performance, and timeless design. Whether you own a sporty Polo or a premium Tiguan, safeguarding your vehicle starts with the right insurance plan. This guide walks you through everything you need to know about insuring a Volkswagen car, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.

                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/wolksinsurance.jpeg"
                alt="Volkswagen Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Volkswagen in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Volkswagen India offers a lineup known for premium build,
            turbocharged performance, and modern design. It appeals to customers
            who value safety, reliability, and sophisticated styling.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Popular VW models on Indian roads include: Volkswagen Polo
            (discontinued but still widely owned): Compact hatchback with strong
            dynamics, Volkswagen Virtus: Mid-size sedan with stylish design and
            TSI engine, Volkswagen Taigun: Compact SUV with high ground
            clearance and connected features, Volkswagen Tiguan: Premium
            5-seater SUV with 4MOTION AWD and DSG gearbox. Volkswagen vehicles
            are performance-driven and tech-equipped, making comprehensive
            insurance a necessity to avoid high repair and service bills.
          </p>
        </div>
      </div>
         <div className="p-4 sm:p-8 bg-white">
                                                <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                                  Choose Your Car Brand
                                                </h2>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                            {volkswageninsurance.map((calc, index) => (
                                              <motion.div
                                                key={index}
                                                whileHover={{ scale: 1.04 }}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                              >
                                                <Link
                                                  to={calc.path}
                                                  className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                                >
                                                  <img
                                                    src={calc.image}
                                                    alt={calc.name}
                                                    className="w-16 h-16 object-contain mb-2"
                                                  />
                                                  <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                                    {calc.name}
                                                  </span>
                                                </Link>
                                              </motion.div>
                                            ))}
                                          </div>
                                          </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Volkswagen Owners Need Comprehensive Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Volkswagen cars feature refined interiors, turbocharged petrol
            engines, and high-quality electronics. These factors, while
            enhancing the drive, also increase repair costs. The right insurance
            ensures financial cover for:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Accidents and collision-related damage</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft, vandalism, or fire</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>
                Natural disasters like floods, landslides, or cyclones
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party injury or property damage claims</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>Expensive spare parts and electronic systems</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            With a comprehensive plan, you also gain access to cashless claims
            at VW-authorized service centers.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Volkswagen Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here is a look at Volkswagen’s key models along with their estimated
          Insured Declared Value (IDV) and starting comprehensive insurance
          premiums:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Volkswagen Polo",
                category: "Premium Hatchback",
                idv: "6–9 Lakh",
                premium: "₹3,500 to ₹5,000",
              },
              {
                model: "Volkswagen Virtus",
                category: "Mid-size Sedan",
                idv: "10–17 Lakh",
                premium: "₹4,800 to ₹7,500",
              },
              {
                model: "Volkswagen Taigun",
                category: "Compact SUV",
                idv: "11–18 Lakh",
                premium: "₹5,000 to ₹8,200",
              },
              {
                model: "Volkswagen Tiguan",
                category: "Premium SUV",
                idv: "28–34 Lakh",
                premium: "₹7,800 to ₹11,500",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  [
                    "Volkswagen Polo",
                    "Premium Hatchback",
                    "6–9 Lakh",
                    "₹3,500 to ₹5,000",
                  ],
                  [
                    "Volkswagen Virtus",
                    "Mid-size Sedan",
                    "10–17 Lakh",
                    "₹4,800 to ₹7,500",
                  ],
                  [
                    "Volkswagen Taigun",
                    "Compact SUV",
                    "11–18 Lakh",
                    "₹5,000 to ₹8,200",
                  ],
                  [
                    "Volkswagen Tiguan",
                    "Premium SUV",
                    "28–34 Lakh",
                    "₹7,800 to ₹11,500",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons Recommended for Volkswagen Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          To better protect your Volkswagen, consider the following add-on
          covers:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Companies for Volkswagen Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          These insurers are known for VW-friendly policies and strong claim
          support:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Reliable online service with high claim settlement ratio.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Customizable plans with useful add-ons for performance vehicles.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Preferred for mid-size and premium cars with wide garage networks.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Reliance General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Competitive pricing and digital claim tracking.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Strong service support and NCB retention features.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Save on Volkswagen Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Keep these strategies in mind to lower your insurance premium:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Maintain a claim-free year to enjoy No Claim Bonus (NCB).
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Opt for voluntary deductibles if you're confident about your
                  driving habits.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare online quotes annually at renewal.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install AIS-140-compliant anti-theft systems.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Renew your policy on time to avoid a break in coverage or loss
                  of NCB.
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Volkswagen Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Ensure these are handy when buying or renewing your insurance:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Vehicle Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance document (for renewal)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Valid Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Proof of identity (Aadhaar, PAN, etc.)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Recent passport photo (for offline application)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Volkswagen Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Volkswagen cars are built for performance, comfort, and
                long-term reliability. Whether you commute in a Polo or cruise
                in a Tiguan, a robust insurance plan protects you against
                unforeseen events on Indian roads.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                By choosing the right insurer, exploring add-ons, and comparing
                plans smartly, you can get full value and complete peace of
                mind. At TheFinMaster, we help you evaluate, compare, and buy
                the best Volkswagen car insurance plans suited to your vehicle
                and budget.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default VolkswagenCarInsurance;
